import React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { Empty } from "@replohq/lattice/components/empty/Empty";
import {
  FieldContent,
  FieldDescription,
  FieldTitle,
} from "@replohq/lattice/components/field/Field";
import { Input } from "@replohq/lattice/components/input/Input";
import {
  ModalContent,
  ModalHeader,
  ModalRoot,
  ModalTitle,
} from "@replohq/lattice/components/modal/Modal";
import { Skeleton } from "@replohq/lattice/components/skeleton/Skeleton";
import { Spinner } from "@replohq/lattice/components/spinner/Spinner";
import { cn } from "@replohq/lattice/utils/cn";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useModal } from "@v2/store/modalStore";
import Stripe from "@v2/svg/billing/Stripe";
import { billingRouter } from "@v2/trpc/routes/billing";
import { fileTemplateRouter } from "@v2/trpc/routes/fileTemplate";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@v2/trpc/routes/nextJSProject";
import { projectRouter } from "@v2/trpc/routes/project";
import { subscriptionRouter } from "@v2/trpc/routes/subscription";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { getProjectLabels } from "@v2/utils/project-labels";
import { routes } from "@v2/utils/router";
import {
  ArrowUpRight,
  Check,
  DollarSign,
  FolderSearch,
  Search,
} from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

export interface SelectProjectForTemplateModalProps {
  pageTemplateId: string;
}

type ModalViewState =
  | "loading"
  | "paidShopifyConversion"
  | "agentSignup"
  | "projectSelection";

const MODAL_TITLES: Record<ModalViewState, string> = {
  loading: "Loading...",
  paidShopifyConversion: "Update Billing Settings to Use Template",
  agentSignup: "Sign up for Replo to use this template",
  projectSelection: "Where will you use this page?",
};

const CONVERSION_FEATURES = [
  "Exclusive access to Replo Builder & AI features, starting with this template",
  "Easier access to view and manage your invoices",
  "Guaranteed safe & secure billing, powered by Stripe",
];

const TemplatePreview: React.FC<{
  previewUrl: string | null;
  templateName: string;
  className?: string;
}> = ({ previewUrl, templateName, className }) => (
  <div className={cn("flex-[0_0_auto] w-[50%] max-w-md", className)}>
    <div className="border-0.5 border-border rounded-2xl overflow-hidden min-h-[400px] h-full flex flex-col">
      <div className="relative flex-1 min-h-0 overflow-hidden p-6 bg-gradient-to-b from-surface from-80% to-white">
        <div className="min-w-full min-h-full flex items-center justify-center">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={templateName}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <Skeleton className="w-full h-full" />
          )}
        </div>
        <div className="absolute -bottom-6 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      </div>
      <div className="bg-white border-t-0.5 border-border p-4 flex-shrink-0">
        <h5 className="text-lg font-editorial text-default text-left">
          {templateName}
        </h5>
      </div>
    </div>
  </div>
);

const ModalFooter: React.FC<{
  onCancel: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  isDisabled?: boolean;
  confirmText: string;
}> = ({ onCancel, onConfirm, isLoading, isDisabled, confirmText }) => (
  <div className="flex flex-col w-full">
    <div className="border-t-0.5 border-border -mx-6" />
    <div className="flex flex-col-reverse sm:flex-row gap-3 w-full sm:justify-end pt-6">
      <Button
        variant="secondary"
        size="base"
        onClick={onCancel}
        disabled={isLoading}
        className="w-full"
        UNSAFE_className="sm:w-auto"
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        size="base"
        onClick={onConfirm}
        disabled={isDisabled}
        loading={isLoading}
        className="w-full"
        UNSAFE_className="sm:w-auto"
      >
        {confirmText}
      </Button>
    </div>
  </div>
);

const LoadingContent: React.FC<{
  templatePreview: { previewUrl: string | null; templateName: string };
}> = ({ templatePreview }) => (
  <div className="flex gap-6">
    <div className="flex-[11] min-w-0 border-0.5 border-border rounded-2xl overflow-hidden flex flex-col items-center justify-center p-6 min-h-[300px]">
      <Spinner className="size-8" variant="primary" />
    </div>
    <TemplatePreview {...templatePreview} className="flex-[9] w-auto" />
  </div>
);

const StripeBillingConversionContent: React.FC<{
  workspaceId: string;
  templatePreview: { previewUrl: string | null; templateName: string };
  onCancel: () => void;
}> = ({ workspaceId, templatePreview, onCancel }) => {
  const { mutate: generateStripeConversionLink, isPending: isGeneratingLink } =
    billingRouter.getStripeConversionLink.useMutation({
      onSuccess: (url) => {
        window.location.href = url;
      },
    });

  const handleConvertClick = () => {
    sessionStorage.setItem("replo_reopen_template_modal", "true");
    generateStripeConversionLink({
      workspaceId,
      nextUrl: window.location.href,
    });
  };

  return (
    <>
      <div className="flex gap-6 h-[400px]">
        <div className="flex-[1_1_0%] min-w-0 border-0.5 border-border rounded-2xl overflow-hidden flex flex-col p-6">
          <div className="p-2 border-0.5 border-border rounded-lg w-fit mb-4">
            <DollarSign className="w-6 h-6" />
          </div>
          <h4 className="header-h6 mb-2">Update to Stripe Billing</h4>
          <p className="text-xs text-muted mb-6">
            This will not result in any extra charges. By updating, you will
            gain:
          </p>
          <div className="flex flex-col gap-3">
            {CONVERSION_FEATURES.map((feature, i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-black">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <p className="text-xs text-muted">{feature}</p>
              </div>
            ))}
          </div>
          <div className="flex-1" />
          <div className="bg-surface border-0.5 border-border rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Stripe className="w-5 h-5" />
              <span className="text-sm font-medium text-default">
                Update in Stripe
              </span>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConvertClick}
              loading={isGeneratingLink}
              endEnhancer={<ArrowUpRight />}
            >
              Update
            </Button>
          </div>
        </div>
        <TemplatePreview {...templatePreview} />
      </div>
      <div className="flex flex-col w-full">
        <div className="border-t-0.5 border-border -mx-6" />
        <div className="flex gap-3 w-full justify-end pt-6">
          <Button variant="secondary" size="base" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="base" disabled>
            Continue
          </Button>
        </div>
      </div>
    </>
  );
};

const AgentSignupContent: React.FC<{
  templatePreview: { previewUrl: string | null; templateName: string };
  onSignUp: () => void;
}> = ({ templatePreview, onSignUp }) => (
  <>
    <div className="flex justify-center">
      <TemplatePreview {...templatePreview} />
    </div>
    <div className="flex flex-col w-full">
      <div className="border-t-0.5 border-border -mx-6" />
      <div className="flex w-full justify-end pt-6">
        <Button variant="primary" size="base" onClick={onSignUp}>
          Sign up for Replo
        </Button>
      </div>
    </div>
  </>
);

const ProjectSelectionContent: React.FC<{
  userId: string;
  pageTemplateId: string;
  templatePreview: { previewUrl: string | null; templateName: string };
  onSuccess: (projectId: string, pageId: string) => void;
  onCancel: () => void;
}> = ({ userId, pageTemplateId, templatePreview, onSuccess, onCancel }) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedProjectId, setSelectedProjectId] = React.useState<
    string | null
  >(null);

  const { data: groupedProjects, isLoading: isLoadingProjects } =
    projectRouter.listGroupedByWorkspaceForUser.useQuery({ userId });

  const { mutate: createPageFromTemplate, isPending: isCreatingPage } =
    nextJSProjectRouter.createPageFromTemplate.useMutation({
      onSuccess: (data, variables) => {
        void nextJSProjectUtils.listPages.invalidate({
          projectId: variables.projectId,
        });
        onSuccess(variables.projectId, data.createdFile.id);
      },
    });

  const allProjects = React.useMemo(() => {
    if (!groupedProjects) {
      return [];
    }

    const projects = groupedProjects.flatMap((workspace) => {
      return workspace.projects
        .filter((project) => project.hostingType === "replo")
        .map((project) => ({
          ...project,
          workspaceName: workspace.workspaceName,
        }));
    });

    return projects.sort((projectA, projectB) => {
      const aDate = projectA.lastEditedAt
        ? new Date(projectA.lastEditedAt).getTime()
        : 0;
      const bDate = projectB.lastEditedAt
        ? new Date(projectB.lastEditedAt).getTime()
        : 0;
      return bDate - aDate;
    });
  }, [groupedProjects]);

  const filteredProjects = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return allProjects;
    }

    const query = searchQuery.toLowerCase();
    return allProjects.filter((project) => {
      return (
        project.name?.toLowerCase().includes(query) ||
        project.workspaceName.toLowerCase().includes(query)
      );
    });
  }, [allProjects, searchQuery]);

  const handleConfirm = () => {
    if (selectedProjectId) {
      createPageFromTemplate({
        projectId: selectedProjectId,
        templateId: pageTemplateId,
      });
    }
  };

  return (
    <>
      <div className="flex gap-6 h-[400px]">
        <div className="flex-[1_1_0%] min-w-0 flex flex-col gap-3 w-full">
          <Input
            value={searchQuery}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              return setSearchQuery(event.target.value);
            }}
            placeholder={`Search ${getProjectLabels("replo").singular} or workspace`}
            className="w-full"
            disabled={isCreatingPage}
            size="base"
            autoFocus
            startEnhancer={<Search className="icon-size-default" />}
          />

          <div className="relative flex-1 overflow-hidden">
            <div className="relative h-full overflow-y-auto no-scrollbar">
              {isLoadingProjects && (
                <div className="flex flex-col gap-3 pb-4">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div
                      key={index}
                      className="p-3 border-0.5 border-border rounded-lg"
                    >
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  ))}
                </div>
              )}
              {!isLoadingProjects && filteredProjects.length === 0 && (
                <div className="h-full flex items-center justify-center">
                  <Empty
                    title={`No results found for "${searchQuery}"`}
                    description="Please try a different search"
                    icon={<FolderSearch className="icon-size-default" />}
                    UNSAFE_className="py-[10%]"
                  />
                </div>
              )}
              {!isLoadingProjects && filteredProjects.length > 0 && (
                <div className="flex flex-col gap-3 pb-4">
                  {filteredProjects.map((project) => (
                    <div
                      key={project.id}
                      className={cn(
                        isCreatingPage && "opacity-50 pointer-events-none",
                      )}
                    >
                      <button
                        type="button"
                        className={cn(
                          "border-0.5 border-border flex w-full cursor-pointer break-words rounded-lg text-left font-normal hover:bg-selectable-hover",
                          selectedProjectId === project.id &&
                            "border-primary bg-selectable-selected hover:bg-selectable-selected-hover",
                        )}
                        onClick={() => setSelectedProjectId(project.id)}
                      >
                        <FieldContent className="p-3">
                          <FieldTitle>
                            {project.name ??
                              `Untitled ${getProjectLabels("replo").singularCapitalized}`}
                          </FieldTitle>
                          <FieldDescription>
                            {project.workspaceName}
                          </FieldDescription>
                        </FieldContent>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {(isLoadingProjects || filteredProjects.length > 0) && (
                <div className="sticky -bottom-1 left-0 right-0 h-12 -mb-4 bg-gradient-to-t from-white via-white to-transparent pointer-events-none" />
              )}
            </div>
          </div>
        </div>

        <TemplatePreview {...templatePreview} className="hidden sm:flex" />
      </div>
      <ModalFooter
        onCancel={onCancel}
        onConfirm={handleConfirm}
        isLoading={isCreatingPage}
        isDisabled={!selectedProjectId}
        confirmText={`Add to ${getProjectLabels("replo").singularCapitalized}`}
      />
    </>
  );
};

export const SelectProjectForTemplateModal: React.FC<
  SelectProjectForTemplateModalProps
> = ({ pageTemplateId }) => {
  const { closeModal } = useModal();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const workspace = useCurrentWorkspace();

  const { data: template } = fileTemplateRouter.getPublic.useQuery({
    id: pageTemplateId,
  });

  const { data: userWorkspacesData } =
    workspaceRouter.getUserWorkspacesList.useQuery();
  const { data: groupedProjects, isLoading: isLoadingGroupedProjects } =
    projectRouter.listGroupedByWorkspaceForUser.useQuery(
      currentUser?.user?.id ? { userId: currentUser.user.id } : skipToken,
    );

  const userWorkspaces = userWorkspacesData?.workspaces ?? [];
  const allWorkspacesAreShopifyBilled =
    userWorkspaces.length > 0 &&
    userWorkspaces.every((ws) => ws.paymentProcessor === "shopify");
  const firstNonShopifyWorkspace = userWorkspaces.find(
    (ws) => ws.paymentProcessor !== "shopify",
  );
  let effectiveWorkspaceId: string | null = null;

  // Note (Ezra, 2025-12-17): We pick an effectiveWorkspaceId for billing/subscription decisions with the following priority:
  // - If the current workspace exists and is NOT Shopify-billed, we use it (this is the normal, "already Stripe/non-Shopify" flow).
  // - Otherwise, if the user has any non-Shopify-billed workspace, we fall back to the first one (so the modal can proceed without forcing Shopify billing).
  // - Otherwise, all workspaces are Shopify-billed; in that case we fall back to the first workspace so we can fetch subscription info and, on free tier, trigger the auto-switch to Stripe billing below.
  // TODO (Ezra, 2025-12-17): Implement a better flow for selecting a workspace with more user control, initial designs did not account for all these workspace flows.
  if (workspace?.id && workspace.paymentProcessor !== "shopify") {
    effectiveWorkspaceId = workspace.id;
  } else if (firstNonShopifyWorkspace?.id) {
    effectiveWorkspaceId = firstNonShopifyWorkspace.id;
  } else if (allWorkspacesAreShopifyBilled && userWorkspaces[0]?.id) {
    effectiveWorkspaceId = userWorkspaces[0].id;
  }
  const v2ProjectCount =
    groupedProjects?.flatMap((ws) => {
      return ws.projects.filter((project) => project.hostingType === "replo");
    }).length ?? 0;

  const { data: subscription, isLoading: isLoadingSubscription } =
    subscriptionRouter.getActiveSubscription.useQuery(
      allWorkspacesAreShopifyBilled && effectiveWorkspaceId
        ? { workspaceId: effectiveWorkspaceId }
        : skipToken,
    );
  const isFreeTier = !subscription || subscription.billingTier === "free";

  const { mutate: updatePaymentProcessor, isPending: isSwitchingBilling } =
    workspaceRouter.updatePaymentProcessor.useMutation({
      onSuccess: () => {
        void workspaceUtils.getUserWorkspacesList.invalidate();
      },
    });

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (
      allWorkspacesAreShopifyBilled &&
      !isLoadingSubscription &&
      isFreeTier &&
      effectiveWorkspaceId &&
      !isSwitchingBilling
    ) {
      updatePaymentProcessor({
        workspaceId: effectiveWorkspaceId,
        paymentProcessor: "stripe",
      });
    }
  }, [
    allWorkspacesAreShopifyBilled,
    isLoadingSubscription,
    isFreeTier,
    effectiveWorkspaceId,
    isSwitchingBilling,
    updatePaymentProcessor,
  ]);

  const viewState: ModalViewState = (() => {
    if (
      allWorkspacesAreShopifyBilled &&
      (isLoadingSubscription || isSwitchingBilling)
    ) {
      return "loading";
    }
    if (allWorkspacesAreShopifyBilled && !isFreeTier) {
      return "paidShopifyConversion";
    }
    if (isLoadingGroupedProjects || v2ProjectCount === null) {
      return "projectSelection";
    }
    if (v2ProjectCount === 0) {
      return "agentSignup";
    }
    return "projectSelection";
  })();

  const templatePreview = {
    previewUrl: template?.desktopPreviewUrl ?? null,
    templateName: template?.name ?? "Template",
  };

  const handleSuccess = (projectId: string, pageId: string) => {
    sessionStorage.removeItem("replo_pending_template_id");
    closeModal({ type: "selectProjectForTemplateModal" });
    navigate(
      `${generatePath(routes.shops.chat, { projectId })}?pageId=${pageId}`,
    );
  };

  const handleCancel = () => {
    sessionStorage.removeItem("replo_pending_template_id");
    closeModal({ type: "selectProjectForTemplateModal" });
  };

  const handleSignUp = () => {
    sessionStorage.removeItem("replo_pending_template_id");
    closeModal({ type: "selectProjectForTemplateModal" });
    window.location.assign("/beta/enable?source=shared-template");
  };

  const renderContent = () => {
    return exhaustiveSwitch(viewState)({
      loading: <LoadingContent templatePreview={templatePreview} />,
      paidShopifyConversion: (
        <StripeBillingConversionContent
          workspaceId={effectiveWorkspaceId!}
          templatePreview={templatePreview}
          onCancel={handleCancel}
        />
      ),
      agentSignup: (
        <AgentSignupContent
          templatePreview={templatePreview}
          onSignUp={handleSignUp}
        />
      ),
      projectSelection: (
        <ProjectSelectionContent
          userId={currentUser.user?.id ?? ""}
          pageTemplateId={pageTemplateId}
          templatePreview={templatePreview}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      ),
    });
  };

  return (
    <ModalRoot
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          sessionStorage.removeItem("replo_pending_template_id");
          closeModal({ type: "selectProjectForTemplateModal" });
        }
      }}
    >
      <ModalContent size="lg" UNSAFE_className="max-w-[90vw] w-[810px]">
        <ModalHeader>
          <ModalTitle>{MODAL_TITLES[viewState]}</ModalTitle>
        </ModalHeader>
        {renderContent()}
      </ModalContent>
    </ModalRoot>
  );
};
